import type { SgdfUserDetails } from "@sgdf/client";
import {
  authJwtCreateCreate,
  authJwtRefreshCreate,
  authUsersResetPasswordConfirmCreate,
  authUsersResetPasswordCreate,
  usersMeRetrieve,
} from "@sgdf/client";
import { configureApiClient } from "../configuration";
import { DateTime } from "luxon";
import { jwtDecode } from "jwt-decode";

// When several requests fire in parallel and the access token is expired, each
// interceptor invocation would otherwise call `refreshToken` independently. With
// rotating refresh tokens that races: only the first call succeeds and the rest
// fail with a now-invalid refresh token. Share a single in-flight promise so
// concurrent callers await the same refresh.
let inFlightRefresh: Promise<{
  access: string;
  refresh?: string;
} | null> | null = null;

export const authenticationService = {
  login: async (user: { username: string; password: string }) => {
    const { data, error } = await authJwtCreateCreate({
      body: {
        username_or_email: user.username,
        password: user.password,
      },
    });

    if (error || !data) {
      return Promise.reject({
        non_field_errors: ["message.user_not_authorized_backoffice"],
      });
    }

    const { access, refresh } = data;
    if (access && refresh) {
      localStorage.setItem("access_token", access);
      localStorage.setItem("refresh_token", refresh);
      configureApiClient();
    } else {
      return Promise.reject({
        non_field_errors: ["message.user_not_authorized_backoffice"],
      });
    }
  },

  logout: (meetingId?: string) => {
    localStorage.removeItem("current_user");
    localStorage.removeItem("last_user_update");
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");

    if (meetingId) {
      localStorage.removeItem(`qrcode_token:${meetingId}`);
    }

    localStorage.removeItem("current_meeting");
  },

  resetPassword: async (email: string) => {
    const { error } = await authUsersResetPasswordCreate({
      body: { email },
    });
    if (error) throw error;
  },

  resetPasswordConfirm: async (
    uid: string,
    token: string,
    newPassword: string,
    reNewPassword: string,
  ) => {
    const { error } = await authUsersResetPasswordConfirmCreate({
      body: {
        uid,
        token,
        new_password: newPassword,
        re_new_password: reNewPassword,
      },
    });
    if (error) throw error;
  },

  scanCode: ({
    qrCodeToken,
    meetingId,
  }: {
    qrCodeToken: string;
    meetingId: string;
  }) => {
    localStorage.setItem(`qrcode_token:${meetingId}`, qrCodeToken);
    localStorage.setItem("current_meeting", meetingId);
  },

  refreshToken: async () => {
    if (inFlightRefresh) return inFlightRefresh;

    inFlightRefresh = (async () => {
      const refresh_token = localStorage.getItem("refresh_token");
      if (!refresh_token) return null;

      try {
        const { data } = await authJwtRefreshCreate({
          body: { refresh: refresh_token },
        });

        if (!data) return null;

        localStorage.setItem("access_token", data.access);
        configureApiClient();

        return data;
      } catch (e) {
        console.log("error refreshing tokens", e);
        return null;
      }
    })().finally(() => {
      inFlightRefresh = null;
    });

    return inFlightRefresh;
  },

  forceGetUser: async (): Promise<SgdfUserDetails | null> => {
    if (!localStorage.getItem("access_token")) return null;
    try {
      const { data } = await usersMeRetrieve();
      if (!data) return null;
      localStorage.setItem("current_user", JSON.stringify(data));
      localStorage.setItem("last_user_update", DateTime.now().toISO() ?? "");
      return data;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return null;
    }
  },

  getUser: async (): Promise<SgdfUserDetails | null> => {
    const localUser = localStorage.getItem("current_user");
    let old = null;

    if (localUser && localUser != "undefined") {
      old = JSON.parse(localUser);
    }

    const lastUserUpdate = localStorage.getItem("last_user_update");
    const old_lu = lastUserUpdate
      ? DateTime.fromISO(lastUserUpdate)
      : DateTime.now().plus({ days: -8 });

    if (old === null || old_lu < DateTime.now().plus({ hours: -12 })) {
      return await authenticationService.forceGetUser();
    } else {
      return old;
    }
  },
};

export const tokenManager = {
  decodeToken(token: string) {
    return jwtDecode<{ exp?: number }>(token);
  },
  getTokenExpirationDate(t: string) {
    const decodedToken = this.decodeToken(t);
    if (decodedToken && "exp" in decodedToken && decodedToken.exp) {
      return new Date(decodedToken.exp * 1000);
    }
    return null;
  },
  isTokenExpired(token: string, delay: number) {
    if (!token || "" === token) return true;
    const expDate = this.getTokenExpirationDate(token);
    if (!expDate) return true;
    const currentDate = new Date();
    const milliseconds = delay * 1000;
    const currentDateWithDelay = new Date(currentDate.getTime() + milliseconds);
    return currentDateWithDelay > expDate;
  },
};
